import type { Module } from '@oasis/contracts';
import { useQuery } from '@tanstack/react-query';
import { Blocks, CircleCheck, CircleOff, HeartPulse } from 'lucide-react';
import type { User } from 'oidc-client-ts';
import { useState, type ReactElement } from 'react';

import {
  ActionBar,
  DataTable,
  EmptyState,
  ErrorMessage,
  FilterBar,
  ChipFilter,
  KpiGrid,
  Loading,
  matchesQuery,
  PageHeader,
  StatCard,
  StatusBadge,
  TextInput,
  type Column,
} from '../design/components.js';
import { api } from '../runtime.js';

import { MODULE_STATUS, statusInfo, statusRank, statusRowTone } from './platform.js';

/**
 * Modules installés : état (actif, désactivé, installé), santé déclarée, dépendances et
 * nombre de permissions. Lecture seule, données de `/modules`.
 */
export function ModulesPage({ user }: { user: User }): ReactElement {
  const modules = useQuery<Module[]>({
    queryKey: ['/modules'],
    queryFn: () => api<Module[]>(user, '/modules'),
  });
  const [q, setQ] = useState('');
  const [status, setStatus] = useState('');
  if (modules.isPending) return <Loading />;
  if (modules.isError) return <ErrorMessage error={modules.error} />;
  const all = modules.data;
  const rows = all.filter(
    (m) =>
      (status === '' || m.status === status) && matchesQuery([m.name, ...(m.depends_on ?? [])], q),
  );
  const enabled = all.filter((m) => m.status === 'enabled').length;
  const disabled = all.filter((m) => m.status === 'disabled').length;
  const unhealthy = all.filter((m) => m.health !== undefined && m.health !== 'ok');

  const columns: Column<Module>[] = [
    { key: 'name', header: 'Module', primary: true, sortValue: (m) => m.name },
    { key: 'version', header: 'Version', sortValue: (m) => m.version },
    {
      key: 'status',
      header: 'État',
      sortValue: (m) => m.status,
      render: (m) => {
        const s = MODULE_STATUS[m.status] ?? { label: m.status, tone: 'neutral' as const };
        return <StatusBadge status={m.status} label={s.label} tone={s.tone} />;
      },
    },
    {
      key: 'health',
      header: 'Santé',
      sortValue: (m) => (m.health === undefined ? -1 : statusRank(m.health)),
      render: (m) =>
        m.health === undefined ? '—' : <StatusBadge status={m.health} {...statusInfo(m.health)} />,
    },
    {
      key: 'depends',
      header: 'Dépend de',
      secondary: true,
      sortValue: (m) => (m.depends_on ?? []).join(', '),
      render: (m) => (m.depends_on ?? []).join(', ') || '—',
    },
    {
      key: 'permissions',
      header: 'Permissions',
      align: 'end',
      secondary: true,
      sortValue: (m) => (m.permissions ?? []).length,
      render: (m) => (m.permissions ?? []).length,
    },
  ];

  return (
    <div className="o-stack">
      <PageHeader
        title="Modules installés"
        description="Modules de la plateforme, leur état et leur santé."
        actions={
          <ActionBar
            more={[
              { label: 'Santé de la plateforme', to: '/sante' },
              { label: 'Réglages des modules', to: '/administration/reglages' },
            ]}
          />
        }
      />
      <KpiGrid label="Modules">
        <StatCard label="Installés" icon={Blocks} value={all.length} />
        <StatCard
          label="Actifs"
          icon={CircleCheck}
          value={enabled}
          tone={enabled === all.length ? 'ok' : 'info'}
        />
        <StatCard
          label="Désactivés"
          icon={CircleOff}
          value={disabled}
          tone={disabled === 0 ? undefined : 'warning'}
        />
        <StatCard
          label="Santé en défaut"
          icon={HeartPulse}
          value={unhealthy.length}
          tone={unhealthy.length === 0 ? 'ok' : 'critical'}
          detail={
            unhealthy.length === 0
              ? 'Aucun module en défaut'
              : unhealthy.map((m) => m.name).join(', ')
          }
          link="/sante"
        />
      </KpiGrid>
      <FilterBar
        count={rows.length}
        onReset={
          q === '' && status === ''
            ? undefined
            : () => {
                setQ('');
                setStatus('');
              }
        }
      >
        <TextInput
          label="Rechercher"
          type="search"
          value={q}
          onChange={(e) => {
            setQ(e.target.value);
          }}
        />
      </FilterBar>
      <ChipFilter
        label="État"
        value={status}
        onChange={(v) => {
          setStatus(typeof v === 'string' ? v : (v[0] ?? ''));
        }}
        options={Object.entries(MODULE_STATUS).map(([k, st]) => ({
          value: k,
          label: st.label,
          count: all.filter((m) => m.status === k).length,
        }))}
      />
      <DataTable
        caption="Modules installés"
        columns={columns}
        rows={rows}
        rowKey={(m) => m.name}
        initialSort={{ key: 'name', direction: 'asc' }}
        rowTone={(m) => (m.health === undefined ? undefined : statusRowTone(m.health))}
        empty={<EmptyState title="Aucun module ne correspond." icon={Blocks} />}
      />
    </div>
  );
}
